import { useEffect, useMemo } from "react";

import { supabaseResponsiveImage } from "@/lib/seo";

/**
 * Regex that matches local hero image paths like `/images/hero-birthday-catering.jpg`.
 * Captures the slug portion (e.g. `hero-birthday-catering`).
 */
const LOCAL_HERO_RE = /^\/images\/(hero-[\w-]+)\.jpg$/;

const WIDTHS = [480, 960, 1920] as const;

/**
 * Variant widths and quality for Supabase-hosted covers, sized to keep every cover under
 * a 150 KB budget (#735). The heaviest live cover measured 246 KB at 1440w q70, 141 KB at
 * 1200w q50 and 101 KB at 960w q50. 1200 is also what a 390px phone at 3x picks, so ad
 * traffic gets the largest entry either way, and the covers sit under a dark overlay that
 * hides the lower quality. The endpoint clamps rather than upscales, so a small source
 * just returns its own width (#734).
 */
const HERO_WIDTHS = [480, 960, 1200] as const;
const HERO_QUALITY = 50;

interface HeroImageProps {
  /** The image URL — can be a local `/images/hero-*.jpg` or an external URL. */
  src: string;
  /** Alt text for the image. */
  alt: string;
  /** Whether this hero is above the fold (first visible hero on page load). */
  isAboveFold?: boolean;
  /** Extra classes applied to the outer container. */
  className?: string;
  /**
   * `sizes` for the responsive srcSet. Defaults to `100vw` (a full-bleed hero). Pass a
   * narrower descriptor for grid thumbnails so the browser doesn't fetch a hero-sized
   * variant for a card.
   */
  sizes?: string;
}

/**
 * Extracts the hero slug from a local image path, or returns `null` for
 * external / non-matching URLs.
 */
function extractHeroSlug(src: string): string | null {
  const match = src.match(LOCAL_HERO_RE);
  return match ? match[1] : null;
}

/**
 * Builds `srcSet` strings for optimized local hero variants.
 * Returns `null` for non-local images.
 */
function buildSrcSets(slug: string | null) {
  if (!slug) return null;

  const avifSrcSet = WIDTHS.map(
    (w) => `/images/${slug}-${w}.avif ${w}w`
  ).join(", ");

  const webpSrcSet = WIDTHS.map(
    (w) => `/images/${slug}-${w}.webp ${w}w`
  ).join(", ");

  return { avifSrcSet, webpSrcSet };
}

/**
 * Responsive hero image component.
 *
 * For local `/images/hero-*.jpg` files, renders a `<picture>` with AVIF + WebP
 * srcSets at 480/960/1920px, falling back to the original JPG. For Supabase Storage
 * covers, renders an `<img>` with a 480/960/1200 WebP srcSet built off the `render/image`
 * endpoint. For any other external URL, renders a simple `<img>`.
 *
 * When `isAboveFold` is true the image loads eagerly and a `<link rel="preload">`
 * tag is injected into `<head>` for the AVIF srcSet (the most common LCP
 * candidate across viewports).
 */
export function HeroImage({
  src,
  alt,
  isAboveFold = false,
  className,
  sizes = "100vw",
}: HeroImageProps) {
  const slug = useMemo(() => extractHeroSlug(src), [src]);
  const srcSets = useMemo(() => buildSrcSets(slug), [slug]);

  // Supabase Storage covers: serve a resized WebP from the render endpoint instead of the
  // raw upload. `resize: "contain"` is load-bearing — without it the endpoint keeps the
  // source height and centre-crops, so each srcSet entry would be framed differently.
  const remote = useMemo(() => {
    if (slug) return null; // local image — the AVIF/WebP branch below handles it
    return supabaseResponsiveImage(src, HERO_WIDTHS, { resize: "contain", quality: HERO_QUALITY });
  }, [slug, src]);
  const loading = isAboveFold ? "eager" : "lazy";
  const fetchPriority = isAboveFold ? "high" : undefined;

  // Preload the LCP hero image on mount (per-page, dynamic).
  useEffect(() => {
    if (!isAboveFold || !slug || !srcSets) return;

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.type = "image/avif";
    link.setAttribute("imagesrcset", srcSets.avifSrcSet);
    link.setAttribute("imagesizes", sizes);
    // Use a data attribute so we can identify and clean up later.
    link.dataset.heroPreload = slug;

    // Avoid duplicates if the component re-mounts.
    const existing = document.head.querySelector(
      `link[data-hero-preload="${slug}"]`
    );
    if (!existing) {
      document.head.appendChild(link);
    }

    return () => {
      // Clean up on unmount (SPA navigation).
      const el = document.head.querySelector(
        `link[data-hero-preload="${slug}"]`
      );
      if (el) el.remove();
    };
  }, [isAboveFold, slug, srcSets, sizes]);

  const imgProps = {
    src,
    alt,
    loading: loading as "eager" | "lazy",
    decoding: "async" as const,
    fetchPriority: fetchPriority as "high" | undefined,
    className: `absolute inset-0 w-full h-full object-cover ${className ?? ""}`.trim(),
  };

  if (!srcSets) {
    if (remote) {
      return <img {...imgProps} src={remote.src} srcSet={remote.srcSet} sizes={sizes} />;
    }
    // Non-Supabase external URL — no optimized variants available.
    return <img {...imgProps} />;
  }

  return (
    <picture>
      <source
        type="image/avif"
        srcSet={srcSets.avifSrcSet}
        sizes={sizes}
      />
      <source
        type="image/webp"
        srcSet={srcSets.webpSrcSet}
        sizes={sizes}
      />
      <img {...imgProps} />
    </picture>
  );
}
